/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isPlainObject from 'lodash/isPlainObject';
import { validate as validateJsonSchema } from 'jsonschema';
import noop from 'lodash/noop';

import { MonacoEditorFormik } from 'Common';

const propTypes = {
  /* width of resizable container */
  width: PropTypes.string,

  /* initial height of resizable container */
  height: PropTypes.string,

  fieldId: PropTypes.string.isRequired,

  /* MonacoEditor options passthru.
   * Only a small subset of options that actually use are specified here.
   * For the full set, see:
   * https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.istandaloneeditorconstructionoptions.html
   */
  options: PropTypes.shape({
    readOnly: PropTypes.bool,
    minimap: PropTypes.shape({
      enabled: PropTypes.bool,
    }),
    lineNumbers: PropTypes.bool,
    lineNumbersMinChars: PropTypes.number,
    selectOnLineNumbers: PropTypes.bool,
    folding: PropTypes.bool,
    foldingHighlight: PropTypes.bool,
  }),
  theme: PropTypes.string,

  jsonToFormikModel: PropTypes.func,
  formikModelToJson: PropTypes.func,

  editorWillMount: PropTypes.func,
  editorDidMount: PropTypes.func,

  /**
   * JSON schema that Monaco Editor will use to (semantically) validate editor contents. Can be one of:
   *  - Object (used to parse in a JSON schema object constructed at runtime, e.g. {type: "object", properties: {name: {type: "string"}}})
   *  - String (will be interpreted as a URI to an external JSON schema e.g. "http://json-schema.org/draft-04/schema#")
   *  - undefined (no semantic validation will be performed)
   */
  schema: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  isLoading: PropTypes.bool,
};

const defaultProps = {
  width: '100%',
  height: '300px',
  options: {},
  theme: 'vs-light',

  jsonToFormikModel: x => x,
  formikModelToJson: x => x,

  editorWillMount: noop,
  editorDidMount: noop,

  schema: undefined,
  isLoading: false,
};

const JsonEditorFormik = ({
  jsonToFormikModel,
  formikModelToJson,
  editorWillMount,
  editorDidMount,
  schema,
  ...others
}) => {
  const [monaco, setMonaco] = useState(null);
  const [editor, setEditor] = useState(null);

  // register a JSON schema semantic validator for this specific editor instance
  // (distinguished by editorPath)
  useEffect(() => {
    // wait until monaco, editor and schema objects are initialised
    if (!monaco || !editor || !schema) {
      return;
    }

    // inspired by https://github.com/Microsoft/monaco-editor/issues/38

    // eslint-disable-next-line no-underscore-dangle
    const editorResource = editor._modelData.model._associatedResource;
    const fileMatch = editorResource.path.substring(1);

    // eslint-disable-next-line no-underscore-dangle
    const diagopts = monaco.languages.json.jsonDefaults._diagnosticsOptions;

    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      ...(diagopts || {}),
      schemas: [
        ...(diagopts.schemas || []),
        // schema provided as object
        isPlainObject(schema)
          ? {
              // eslint-disable-next-line no-underscore-dangle
              uri: `${editorResource._formatted}-schema.json`,
              fileMatch: [fileMatch],
              schema,
            }
          : // URI to external schema provided
            {
              uri: schema,
              fileMatch: [fileMatch],
            },
      ],
    });

    // ensure it is disposed on unmount to prevent a memory leak
    // eslint-disable-next-line consistent-return
    return () => {
      // eslint-disable-next-line no-underscore-dangle
      const diagoptsAfter = monaco.languages.json.jsonDefaults._diagnosticsOptions;
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        ...(diagoptsAfter || {}),
        schemas: diagoptsAfter.schemas.filter(x => x.fileMatch[0] !== fileMatch),
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monaco, editor]);

  return (
    <MonacoEditorFormik
      formikModelToString={model => JSON.stringify(formikModelToJson(model), null, 2)}
      stringToFormikModel={string => {
        const json = JSON.parse(string); // first convert the string held by Monaco into JSON (parse errors will throw)
        // now validate the JSON wrt. the schema (validation errors will throw)
        if (schema) {
          const result = validateJsonSchema(json, schema);
          if (!result) {
            throw new Error('JSON schema validation result is undefined');
          }
          if (!result.valid) {
            if (!result.errors) {
              throw new Error('JSON schema validation result.errors is undefined');
            } else if (result.errors.length === 0) {
              throw new Error('JSON schema validation result.errors is empty');
            } else {
              throw result.errors[0];
            }
          }
        }
        return jsonToFormikModel(json);
      }}
      language="json"
      editorWillMount={m => {
        setMonaco(m);
        editorWillMount(m);
      }}
      editorDidMount={e => {
        setEditor(e);
        editorDidMount(e);
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...others}
    />
  );
};

JsonEditorFormik.propTypes = propTypes;
JsonEditorFormik.defaultProps = defaultProps;

export default JsonEditorFormik;
